/**
 * URLs de preview admin que simulan geo/variante sin VPN.
 *
 * Dos mecanismos (se pueden combinar):
 *   1. `?debug=SECRET&country=US&state=FL` — el worker emite __TEMPLATE_CONFIG__
 *      como si el visitor fuera de esa geo (template + variant cascade).
 *   2. `?_preview_template=us-exclusive|video` — el front fuerza el componente
 *      aunque el worker diga otro template (útil si el admin está en AR).
 *
 * `_force_variant` skipa el bucket sticky y carga mux/labels/precio de esa ID.
 */

function readWorkerDebugSecret(): string {
  try {
    const fromEnv = import.meta.env?.VITE_WORKER_DEBUG_SECRET;
    if (typeof fromEnv === "string" && fromEnv.trim()) return fromEnv.trim();
  } catch {
    // Node scripts (tsx regression) — import.meta.env ausente
  }
  return "cm2024xr";
}

/** Paridad con DEBUG_SECRET en cloudflare-worker-juaperrone/worker.js */
export const WORKER_DEBUG_SECRET = readWorkerDebugSecret();

export type AdminPreviewTemplate = "video" | "us-exclusive" | "dual";

export interface AdminGeoPreviewOptions {
  path?: string;
  country?: string;
  state?: string;
  province?: string;
  city?: string;
  forceVariant?: string;
  /** Fuerza render en Index sin depender del template del worker. */
  previewTemplate?: AdminPreviewTemplate;
  /** i18next querystring detector (`?lang=en`). */
  lang?: string;
  /** Incluir ?debug= — necesario para que country/state pisen cf.country en hard nav. */
  includeDebug?: boolean;
  /**
   * Autorización por sesión de super admin: emite `?geo_sim=1` en vez de
   * `?debug=<SECRET>`. El worker verifica la cookie geo_sim_token (firmada por
   * /api/admin/geo-sim-token) en lugar del secreto estático. Cuando es true se
   * ignora includeDebug (no se filtra el secreto al bundle/URL).
   */
  sessionAuth?: boolean;
  /**
   * Token de simulación (firmado por /api/admin/geo-sim-token) → se emite como
   * `?gst=<token>`. Es el camino robusto: el worker lo lee de la URL sin depender
   * de la cookie. Solo tiene efecto con sessionAuth (geo_sim=1).
   */
  token?: string;
  /** `_admin_preview=1` → marca preview admin (gates/iframe layout). El unmute de Mux NO se oculta por este flag. */
  adminLivePreview?: boolean;
  /**
   * Simulate a real VPN / anonymous visitor for this preview.
   * When true (or 'anonymous'/'vpn'), the worker will apply VPN/privacy gates
   * (hideCta, BaseTemplate downgrade, privacy flags) even for authorized admin previews.
   * This lets admins see "what a real VPN user experiences" for the simulated geo.
   * Use `simulateVpn: 'clean'` (or false) to get the previous trusted bypass behavior.
   */
  simulateVpn?: boolean | 'anonymous' | 'vpn' | 'hosting' | 'clean';

  /**
   * Use a real IP address for simulation. When provided (and admin authorized),
   * the worker will perform an IPLocate lookup on this IP to get authentic
   * geolocation + privacy/threat data (is_vpn, is_anonymous, is_hosting, etc.).
   * Combined with manual country/state, this lets you preview "as if this
   * exact real VPN/proxy IP was visiting from the simulated geo".
   * Great for QA using real iplocate data (we can use MCP for examples).
   */
  simulateIp?: string;
}

export function buildAdminGeoPreviewUrl(options: AdminGeoPreviewOptions): string {
  const path = options.path ?? "/";
  const params = new URLSearchParams();

  if (options.sessionAuth) {
    // Super admin: geo_sim=1 marca la intención; el token viaja en ?gst= (robusto,
    // no depende de la cookie).
    params.set("geo_sim", "1");
    if (options.token?.trim()) {
      params.set("gst", options.token.trim());
    }
  } else if (options.includeDebug !== false) {
    params.set("debug", WORKER_DEBUG_SECRET);
  }
  if (options.country?.trim()) {
    params.set("country", options.country.trim().toUpperCase());
  }
  if (options.state?.trim()) {
    params.set("state", options.state.trim().toUpperCase());
  }
  if (options.province?.trim()) {
    params.set("province", options.province.trim());
  }
  if (options.city?.trim()) {
    params.set("city", options.city.trim());
  }
  if (options.forceVariant?.trim()) {
    params.set("_force_variant", options.forceVariant.trim());
  }
  if (options.previewTemplate === "video") {
    params.set("_preview_template", "video");
  } else if (options.previewTemplate === "us-exclusive") {
    params.set("_preview_template", "us-exclusive");
  } else if (options.previewTemplate === "dual") {
    params.set("_preview_template", "dual");
  }
  if (options.adminLivePreview !== false) {
    params.set("_admin_preview", "1");
  }
  if (options.lang?.trim()) {
    params.set("lang", options.lang.trim().toLowerCase());
  }

  // VPN / privacy simulation for realistic admin previews.
  // When set, worker will *apply* the privacy/VPN gates (no bypass) + expose privacy flags.
  if (options.simulateVpn) {
    if (options.simulateVpn === 'clean' || options.simulateVpn === false) {
      params.set('simulate_vpn', 'clean');
    } else if (options.simulateVpn === 'hosting') {
      params.set('simulate_vpn', 'hosting');
    } else {
      // 'anonymous' | 'vpn' | true → treat as anonymous/VPN
      params.set('simulate_vpn', 'anonymous');
    }
  }

  if (options.simulateIp?.trim()) {
    params.set('simulate_ip', options.simulateIp.trim());
  }

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Preview fiel de USExclusiveLanding + variante A/B.
 * Funciona desde Argentina (u otro país): no requiere VPN.
 */
export function buildUsExclusiveVariantPreviewUrl(
  variantId: string,
  options?: {
    state?: string;
    city?: string;
    simulateWorkerGeo?: boolean;
    lang?: string;
    simulateIp?: string;
  },
): string {
  const simulateWorkerGeo = options?.simulateWorkerGeo !== false;
  return buildAdminGeoPreviewUrl({
    path: "/",
    country: simulateWorkerGeo ? "US" : undefined,
    state: options?.state,
    city: options?.city,
    forceVariant: variantId,
    previewTemplate: "us-exclusive",
    includeDebug: simulateWorkerGeo,
    lang: options?.lang,
    simulateVpn: true, // admin previews simulate real VPN by default
    simulateIp: options?.simulateIp,
  });
}

/** Preview / sin _force_variant — cascada real city → state_us → country en USExclusiveLanding. */
export function buildUsExclusiveGeoPreviewUrl(options?: {
  state?: string;
  city?: string;
  lang?: string;
  simulateIp?: string;
}): string {
  return buildAdminGeoPreviewUrl({
    path: "/",
    country: "US",
    state: options?.state,
    city: options?.city,
    previewTemplate: "us-exclusive",
    includeDebug: true,
    lang: options?.lang,
    simulateVpn: true,
    simulateIp: options?.simulateIp,
  });
}